// src/features/patients/components/AdmitPatientModal.tsx
import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
} from "@chakra-ui/react";
import { useCreateAdmission } from "../../../apis/patients";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  appointmentId?: string;
  /** Called with the new admission _id after a successful admit */
  onAdmitted?: (admissionId: string) => void;
};

export default function AdmitPatientModal({
  isOpen,
  onClose,
  patientId,
  appointmentId,
  onAdmitted,
}: Props) {
  const [reason, setReason] = useState("");
  const [ward, setWard] = useState("");
  const [bed, setBed] = useState("");
  const [notes, setNotes] = useState("");

  const { mutate, isPending } = useCreateAdmission();

  const handleAdmit = () => {
    mutate(
      { patientId, appointmentId, reason, ward, bed, notes },
      {
        onSuccess: (created: any) => {
          // 🔍 Debug: log the raw response to see its shape in DevTools
          try {
            console.log(
              "[AdmitPatientModal] created admission response:",
              created
            );
          } catch {
            // ignore logging errors
          }

          // 🔑 Robustly dig out the new admission id from common shapes
          let newId: any =
            created?._id ??
            created?.id ??
            created?.data?._id ??
            created?.data?.id ??
            created?.data?.data?._id ??
            created?.data?.data?.id;

          if (newId && onAdmitted) {
            onAdmitted(String(newId));
          }

          onClose();
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Admit Patient</ModalHeader>
        <ModalBody>
          <Input
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            mb={3}
          />
          <Input
            placeholder="Ward (optional)"
            value={ward}
            onChange={(e) => setWard(e.target.value)}
            mb={3}
          />
          <Input
            placeholder="Bed (optional)"
            value={bed}
            onChange={(e) => setBed(e.target.value)}
            mb={3}
          />
          <Textarea
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} variant="ghost" mr={3}>
            Cancel
          </Button>
          <Button isLoading={isPending} onClick={handleAdmit}>
            Admit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
